import { useState, type ReactNode } from 'react';
import clsx from 'clsx';
import type { Health, HistorySample } from '@shared/domain';
import { HEALTH_LABEL, HEALTH_STYLE } from '@/lib/format';

/* --------------------------------- Card -------------------------------- */
export function Card({ className, children }: { className?: string; children?: ReactNode }) {
  return <div className={clsx('card', className)}>{children}</div>;
}

export function SectionTitle({ title, desc, right }: { title: ReactNode; desc?: ReactNode; right?: ReactNode }) {
  return (
    <div className="mb-3 flex items-end justify-between gap-4">
      <div>
        <h3 className="text-[17px] font-semibold text-slate-800">{title}</h3>
        {desc ? <p className="mt-1 text-[13px] text-slate-500">{desc}</p> : null}
      </div>
      {right}
    </div>
  );
}

export function PageHeader({ title, subtitle, meta, right }: { title: ReactNode; subtitle?: ReactNode; meta?: ReactNode; right?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h2 className="text-[26px] font-bold tracking-tight text-slate-900">{title}</h2>
        {subtitle ? <p className="mt-1.5 text-[14px] text-slate-500">{subtitle}</p> : null}
        {meta ? <div className="mt-1 text-[13px] text-slate-400">{meta}</div> : null}
      </div>
      {right}
    </div>
  );
}

/* ------------------------------- Pills/Badge --------------------------- */
export function StatusPill({ health, className }: { health: Health; className?: string }) {
  return <span className={clsx('pill', HEALTH_STYLE[health], className)}>{HEALTH_LABEL[health]}</span>;
}

export function Badge({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={clsx('pill', 'bg-slate-100 text-slate-600 border border-slate-200', className)}>{children}</span>;
}

export function Chip({ active, children, onClick }: { active?: boolean; children: ReactNode; onClick?: () => void }) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'rounded-lg border px-3.5 py-2 text-sm transition-colors',
        active ? 'border-brand-500 bg-brand-50 text-brand-700 font-semibold' : 'border-hairline bg-white text-slate-600 hover:bg-slate-50',
      )}
    >
      {children}
    </button>
  );
}

/* ------------------------------ MetricCard ----------------------------- */
export function MetricCard({ label, value, unit, hint, tone }: { label: ReactNode; value: ReactNode; unit?: ReactNode; hint?: ReactNode; tone?: 'default' | 'brand' | 'warn' | 'alarm' }) {
  const toneCls =
    tone === 'warn' ? 'text-warn-fg' : tone === 'alarm' ? 'text-alarm-fg' : tone === 'brand' ? 'text-brand-600' : 'text-slate-900';
  return (
    <Card className="p-5">
      <div className="text-[13px] text-slate-500">{label}</div>
      <div className="mt-2 flex items-baseline gap-1.5">
        <span className={clsx('text-[30px] font-bold leading-none tabular-nums', toneCls)}>{value}</span>
        {unit ? <span className="text-[13px] text-slate-400">{unit}</span> : null}
      </div>
      {hint ? <div className="mt-2 text-[12px] text-slate-400">{hint}</div> : null}
    </Card>
  );
}

/* -------------------------------- Toggle ------------------------------- */
export function Toggle({ checked, disabled, onChange, label }: { checked: boolean; disabled?: boolean; onChange?: (v: boolean) => void; label?: string }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      aria-label={label ?? '开关'}
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className={clsx(
        'relative inline-flex h-8 w-16 items-center rounded-full border text-xs font-medium transition-colors',
        checked ? 'justify-end border-ok-border bg-ok-bg' : 'justify-start border-hairline bg-slate-100',
        disabled && 'cursor-not-allowed opacity-60',
      )}
    >
      <span className={clsx('mx-1.5', checked ? 'text-ok-fg' : 'text-slate-500')}>{checked ? '开' : '关'}</span>
    </button>
  );
}

/* ------------------------------- Skeleton ------------------------------ */
export function Skeleton({ className }: { className?: string }) {
  return <div className={clsx('animate-pulse rounded-md bg-slate-100', className)} />;
}

export function EmptyHint({ children }: { children: ReactNode }) {
  return <div className="flex flex-col items-center justify-center gap-2 py-10 text-[13px] text-slate-400">{children}</div>;
}

/** L1: unified loading / empty / error / content state so pages render consistently. */
export function AsyncState({
  isLoading,
  error,
  isEmpty,
  empty,
  onRetry,
  loadingText = '加载数据中…',
  children,
}: {
  isLoading?: boolean;
  error?: unknown;
  isEmpty?: boolean;
  empty?: ReactNode;
  onRetry?: () => void;
  loadingText?: string;
  children: ReactNode;
}) {
  if (error) {
    return (
      <div role="alert" className="flex flex-col items-center gap-2 rounded-lg bg-alarm-bg px-4 py-6 text-[13px] text-alarm-fg">
        <span>加载失败：{error instanceof Error ? error.message : String(error)}</span>
        {onRetry ? (
          <button onClick={onRetry} className="btn btn-ghost mt-1 text-[13px]">
            重试
          </button>
        ) : null}
      </div>
    );
  }
  if (isLoading && (isEmpty ?? true)) return <div className="py-10 text-center text-[13px] text-slate-400">{loadingText}</div>;
  if (isEmpty) return <>{empty ?? <EmptyHint>暂无数据</EmptyHint>}</>;
  return <>{children}</>;
}

/* ------------------------------- DataTable ----------------------------- */
export interface Column<T> {
  key: string;
  header: ReactNode;
  render?: (row: T, index: number) => ReactNode;
  align?: 'left' | 'right' | 'center';
  className?: string;
}

/**
 * DataTable with optional windowed virtualization (L1): lists longer than
 * `virtualize` rows (default 100) render only the visible slice inside a fixed
 * scroll viewport with top/bottom spacer rows, so huge tables stay smooth.
 * Small tables render normally (back-compatible).
 */
export function DataTable<T>({
  columns,
  rows,
  empty,
  virtualize = 100,
  rowHeight = 44,
  viewportHeight = 480,
}: {
  columns: Column<T>[];
  rows: T[];
  empty?: ReactNode;
  virtualize?: number | false;
  rowHeight?: number;
  viewportHeight?: number;
}) {
  const overscan = 8;
  const [scrollTop, setScrollTop] = useState(0);
  const canVirt = virtualize !== false && rows.length > virtualize;

  const head = (
    <thead>
      <tr className="border-b border-hairline bg-slate-50/60">
        {columns.map((c) => (
          <th key={c.key} scope="col" className={clsx('th', c.align === 'right' && 'text-right', c.align === 'center' && 'text-center', c.className)}>
            {c.header}
          </th>
        ))}
      </tr>
    </thead>
  );
  const cell = (c: Column<T>, row: T, i: number) => (
    <td key={c.key} className={clsx('td', c.align === 'right' && 'text-right', c.align === 'center' && 'text-center')}>
      {c.render ? c.render(row, i) : String((row as Record<string, unknown>)[c.key] ?? '')}
    </td>
  );

  if (rows.length === 0) {
    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          {head}
          <tbody>
            <tr>
              <td className="td" colSpan={columns.length}>
                {empty ?? <EmptyHint>暂无数据</EmptyHint>}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  if (!canVirt) {
    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          {head}
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-b border-hairline/70 last:border-0 hover:bg-slate-50/50">
                {columns.map((c) => cell(c, row, i))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const total = rows.length;
  const winRows = Math.ceil(viewportHeight / rowHeight) + overscan * 2;
  const start = Math.max(0, Math.min(total - winRows, Math.floor(scrollTop / rowHeight) - overscan));
  const end = Math.min(total, start + winRows);
  const topPad = start * rowHeight;
  const bottomPad = (total - end) * rowHeight;

  return (
    <div className="overflow-auto" style={{ maxHeight: viewportHeight }} onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}>
      <table className="w-full border-collapse">
        {head}
        <tbody>
          {topPad > 0 ? (
            <tr style={{ height: topPad }} aria-hidden>
              <td colSpan={columns.length} className="p-0" />
            </tr>
          ) : null}
          {rows.slice(start, end).map((row, i) => (
            <tr key={start + i} style={{ height: rowHeight }} className="border-b border-hairline/70 last:border-0 hover:bg-slate-50/50">
              {columns.map((c) => cell(c, row, start + i))}
            </tr>
          ))}
          {bottomPad > 0 ? (
            <tr style={{ height: bottomPad }} aria-hidden>
              <td colSpan={columns.length} className="p-0" />
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------ ProcessChain --------------------------- */
export function ProcessChain({ nodes }: { nodes: string[] }) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl bg-slate-100/70 p-4">
      {nodes.map((n, i) => (
        <div key={n} className="flex items-center gap-2">
          <div className="rounded-lg bg-ink-800 px-3.5 py-2.5 text-[13px] font-medium text-white shadow-sm">{n}</div>
          {i < nodes.length - 1 ? <span className="text-slate-400">→</span> : null}
        </div>
      ))}
    </div>
  );
}

/* ------------------------------- Sparkline ----------------------------- */
export function Sparkline({ data, width = 120, height = 32, stroke = '#17a8ab' }: { data: number[]; width?: number; height?: number; stroke?: string }) {
  if (data.length < 2) return <svg width={width} height={height} />;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1;
  const pts = data
    .map((v, i) => `${(i / (data.length - 1)) * width},${height - ((v - min) / span) * (height - 4) - 2}`)
    .join(' ');
  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline points={pts} fill="none" stroke={stroke} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ------------------------------ SubsystemCard -------------------------- */
export function SubsystemCard({ name, health, onOpen }: { name: string; health: Health; onOpen?: () => void }) {
  return (
    <Card className="flex flex-col justify-between p-4">
      <div className="flex items-start justify-between gap-2">
        <span className="text-[15px] font-semibold text-slate-800">{name}</span>
        <StatusPill health={health} />
      </div>
      <button onClick={onOpen} className="mt-4 self-start text-[13px] text-slate-500 hover:text-brand-600">
        设备档案 →
      </button>
    </Card>
  );
}

/* ------------------------------- TrendChart ---------------------------- */
export function TrendChart({
  title,
  samples,
  unit,
  color = '#17a8ab',
  height = 84,
}: {
  title: ReactNode;
  samples: HistorySample[];
  unit?: string;
  color?: string;
  height?: number;
}) {
  const W = 320;
  const H = height;
  const vals = samples.map((s) => s.v);
  const last = vals[vals.length - 1];
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  const pts = vals
    .map((v, i) => `${(i / Math.max(1, vals.length - 1)) * W},${H - ((v - min) / span) * (H - 12) - 6}`)
    .join(' ');
  const area = vals.length > 1 ? `0,${H} ${pts} ${W},${H}` : '';

  return (
    <Card className="p-5">
      <div className="flex items-baseline justify-between">
        <span className="text-[14px] font-semibold text-slate-700">{title}</span>
        <span className="text-[22px] font-bold tabular-nums text-slate-900">
          {last === undefined ? '—' : last.toFixed(2)}
          {unit ? <span className="ml-1 text-[12px] font-normal text-slate-400">{unit}</span> : null}
        </span>
      </div>
      {vals.length > 1 ? (
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="mt-2 h-[84px] w-full">
          <polygon points={area} fill={color} opacity={0.12} />
          <polyline points={pts} fill="none" stroke={color} strokeWidth={2} vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
        </svg>
      ) : (
        <div className="mt-3 text-[13px] text-slate-400">数据采集中…（实时曲线）</div>
      )}
      <div className="mt-1 flex justify-between text-[11px] text-slate-400">
        <span>min {min === Infinity ? '—' : min.toFixed(2)}</span>
        <span>max {max === -Infinity ? '—' : max.toFixed(2)}</span>
      </div>
    </Card>
  );
}

/* ----------------------------- ProcessDiagram -------------------------- */
export interface ProcessNode {
  label: string;
  value?: string;
  sub?: string;
  status?: Health;
}

/** Lightweight SVG/CSS process schematic with live register read-outs. */
export function ProcessDiagram({ nodes }: { nodes: ProcessNode[] }) {
  const dot = (h?: Health) => (h === 'alarm' ? '#c53030' : h === 'warning' ? '#b7791f' : h === 'normal' ? '#0f8a4f' : '#94a3b8');
  return (
    <div className="flex flex-wrap items-stretch gap-1 rounded-xl bg-slate-100/70 p-4">
      {nodes.map((n, i) => (
        <div key={n.label} className="flex items-center gap-1">
          <div className="min-w-[92px] rounded-lg bg-ink-800 px-3 py-2.5 text-white shadow-sm">
            <div className="flex items-center gap-1.5">
              <span className="inline-block h-2 w-2 rounded-full" style={{ background: dot(n.status) }} />
              <span className="text-[12px] opacity-80">{n.label}</span>
            </div>
            {n.value ? <div className="mt-1 text-[15px] font-bold tabular-nums">{n.value}</div> : null}
            {n.sub ? <div className="text-[10px] opacity-60">{n.sub}</div> : null}
          </div>
          {i < nodes.length - 1 ? (
            <svg width="22" height="12" className="text-slate-400" aria-hidden="true">
              <line x1="0" y1="6" x2="16" y2="6" stroke="currentColor" strokeWidth="2" />
              <polygon points="16,2 22,6 16,10" fill="currentColor" />
            </svg>
          ) : null}
        </div>
      ))}
    </div>
  );
}
